import { NetworkService } from './network-service';

const API_BASE_URL = process.env.REACT_APP_API_EXECUTION_ADDRESS;

/**
 * Comm Center endpoints: the threaded communications feed of a project and the
 * activities generated from it.
 *
 * Notes on the current backend contract (UAT, verified against /openapi.json):
 * - the communications list returns root messages only; `GET .../replies` lists
 *   the thread of one message and the parent keeps carrying the counter;
 * - the `filter` query parameter of the list endpoints matches nothing at the
 *   moment, so filtering is done client side and the parameter is never sent;
 * - the activity lists take a server side scope: `cueSheetItemId` for the cues,
 *   `entityType` + `entityId` for videos / rec sessions / clips, `projectId` on
 *   `/me/activities`. The two id params repeat (OR between the ids, AND with
 *   everything else); `entityType` stays single and qualifies the ids;
 * - `includeEvents=true` adds the informative events (team_join, cue_updated,
 *   completed_cue, deadline) to the project list, which leaves them out by
 *   default;
 * - `MessageRead.cueSheetItemIds` are the cues its `cueRefs` were resolved onto
 *   (label, case insensitive). An homonym — a label shared by several cues of
 *   the project — is deliberately left unresolved: its id is simply missing.
 *   `MessageCreate.cue_sheet_item_ids` closes that gap: the ids are validated on
 *   the project (a foreign one answers 400) and merged, deduplicated, with the
 *   ones extracted from the text;
 * - the communications list takes `cueSheetItemId` too, repeatable, with the
 *   same semantics as on the activities (OR between the ids, AND with the rest);
 * - the activity flow is generate -> confirm(assign) -> submit-review -> approve;
 * - read / archived are per user flags of the feed and live on `/me/activities`,
 *   but they are echoed on every `ActivityRead`, project scoped lists included;
 * - `generate-activity` and `confirm` run an LLM / send notifications and can
 *   take several seconds — always drive them from a mutation with a pending state.
 *
 * Every write here is gated by the role of the caller inside the project
 * (`capabilities.py`): a denied action answers 403. The UI mirrors those rules
 * in helpers/projectRoles.js so the control is not offered in the first place.
 */
/**
 * A query param the API accepts several times over, as [key, value] pairs.
 * A scalar is taken as a list of one, and an empty list sends nothing.
 */
const repeated = (key, value) => (value === null || value === undefined
    ? []
    : (Array.isArray(value) ? value : [value]).filter(Boolean).map(item => [key, item]));

export default class CommunicationsService {

    // ------------------------------------------------------------ communications

    /**
     * Root messages of a project, newest first.
     * @param {object} scope  { cueSheetItemId }: only the messages referring to
     *   one of those cues, resolved by id.
     * @returns {Promise<{pageId:number,pageSize:number,total:number,items:array}>}
     */
    static async listCommunicationsAsync(projectId, { pageId = 0, pageSize = 50, cueSheetItemId = null } = {}) {
        return await NetworkService.getAsync(`${API_BASE_URL}/projects/${projectId}/communications`, [
            ['pageId', pageId],
            ['pageSize', pageSize],
            ...repeated('cueSheetItemId', cueSheetItemId),
        ]);
    }

    /**
     * Replies of one thread, oldest first.
     * @returns {Promise<{pageId:number,pageSize:number,total:number,items:array}>}
     */
    static async listRepliesAsync(messageId, { pageId = 0, pageSize = 100 } = {}) {
        return await NetworkService.getAsync(`${API_BASE_URL}/communications/${messageId}/replies`, [
            ['pageId', pageId],
            ['pageSize', pageSize],
        ]);
    }

    /**
     * @param {object} body {body, cue_refs?, cue_sheet_item_ids?, mentions?, parent_id?}
     */
    static async createCommunicationAsync(projectId, body) {
        return await NetworkService.postAsync(`${API_BASE_URL}/projects/${projectId}/communications`, body);
    }

    /** Semantic search over the project messages. */
    static async searchCommunicationsAsync(projectId, q, k = 20) {
        return await NetworkService.getAsync(`${API_BASE_URL}/projects/${projectId}/communications/search`, [
            ['q', q],
            ['k', k],
        ]);
    }

    /**
     * Marks a thread as resolved. The flag is a toggle-less set on the backend.
     * Producers and directors close any thread, everybody else only their own.
     */
    static async resolveCommunicationAsync(messageId) {
        return await NetworkService.postAsync(`${API_BASE_URL}/communications/${messageId}/resolve`, {});
    }

    /** LLM summary of a thread. */
    static async getThreadSummaryAsync(projectId, threadId) {
        return await NetworkService.getAsync(`${API_BASE_URL}/assistant/thread-summary`, [
            ['projectId', projectId],
            ['threadId', threadId],
        ]);
    }

    // ---------------------------------------------------------------- activities

    /**
     * Turns a message into a draft activity (LLM, a few seconds).
     * @param {string|null} name optional title (max 200) that makes the activity
     *   easy to spot in the Dashboard / project feeds; left out, it stays null.
     */
    static async generateActivityAsync(messageId, name = null) {
        return await NetworkService.postAsync(
            `${API_BASE_URL}/communications/${messageId}/generate-activity`,
            name ? { name } : {}
        );
    }

    /**
     * @param {string} tab 'all' | 'review' — 'review' returns the `in_review` ones.
     * @param {object} scope  { cueSheetItemId } or { entityType, entityId }: what
     *   the feed is narrowed to. Empty values are dropped by the network layer.
     */
    static async listActivitiesAsync(projectId, {
        tab = 'all',
        pageId = 0,
        pageSize = 50,
        cueSheetItemId = null,
        entityType = null,
        entityId = null,
        includeEvents = false,
    } = {}) {
        return await NetworkService.getAsync(`${API_BASE_URL}/projects/${projectId}/activities`, [
            ['tab', tab],
            ['pageId', pageId],
            ['pageSize', pageSize],
            ...repeated('cueSheetItemId', cueSheetItemId),
            ['entityType', entityType],
            ...repeated('entityId', entityId),
            ...(includeEvents ? [['includeEvents', 'true']] : []),
        ]);
    }

    /**
     * Cross project feed: every activity of the projects the user belongs to,
     * plus the informative events (team_join, cue_updated, completed_cue,
     * deadline) that the project scoped list deliberately leaves out.
     *
     * @param {boolean|null} archived   null lists everything, a boolean filters
     * @param {string|null}  projectId  narrows the feed to one project, server side
     */
    static async listMyActivitiesAsync({
        archived = null,
        pageId = 0,
        pageSize = 50,
        projectId = null,
        cueSheetItemId = null,
        entityType = null,
        entityId = null,
    } = {}) {
        return await NetworkService.getAsync(`${API_BASE_URL}/me/activities`, [
            ['pageId', pageId],
            ['pageSize', pageSize],
            // `false` has to survive buildQueryString, which drops '' / null only.
            ...(archived === null ? [] : [['archived', String(archived)]]),
            ['projectId', projectId],
            ...repeated('cueSheetItemId', cueSheetItemId),
            ['entityType', entityType],
            ...repeated('entityId', entityId),
        ]);
    }

    /** Edits a draft activity. Only `draft` activities are editable. */
    static async editActivityAsync(activityId, body) {
        return await NetworkService.postAsync(`${API_BASE_URL}/activities/${activityId}`, body);
    }

    /** draft -> assigned. */
    static async confirmActivityAsync(activityId, assigneeUserId = null) {
        return await NetworkService.postAsync(`${API_BASE_URL}/activities/${activityId}/confirm`, {
            assignee_user_id: assigneeUserId,
        });
    }

    /** assigned -> in_review. Called by the assignee, a producer or a director. */
    static async submitActivityForReviewAsync(activityId) {
        return await NetworkService.postAsync(`${API_BASE_URL}/activities/${activityId}/submit-review`, {});
    }

    /** assigned | in_review -> completed. */
    static async approveActivityAsync(activityId) {
        return await NetworkService.postAsync(`${API_BASE_URL}/activities/${activityId}/approve`, {});
    }

    // ------------------------------------------------------------- feed per user

    /** Read / archived are personal flags: they never change what others see. */
    static async setActivityReadAsync(activityId, read) {
        return await NetworkService.postAsync(
            `${API_BASE_URL}/me/activities/${activityId}/${read ? 'read' : 'unread'}`,
            {}
        );
    }

    static async setActivityArchivedAsync(activityId, archived) {
        return await NetworkService.postAsync(
            `${API_BASE_URL}/me/activities/${activityId}/${archived ? 'archive' : 'unarchive'}`,
            {}
        );
    }
}
